import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  CalendarDays,
  PackagePlus,
  Pencil,
  ShoppingCart,
  CheckCircle2,
  CircleDot,
  XCircle,
  AlertTriangle,
  RefreshCw, BarChart3,
  Tag,
  Warehouse,
} from "lucide-react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";
import { formatDateTime } from "../utils/datetime";

const STATUS_MAP = {
  draft:  { label: "Draft",   tone: "zinc",   icon: CircleDot },
  active: { label: "Aktif",   tone: "emerald",  icon: CheckCircle2 },
  closed: { label: "Selesai", tone: "rose",     icon: XCircle },
};

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth.user?.role === "admin";
  const dialog = useDialog();

  const [event, setEvent]               = useState(null);
  const [openSession, setOpenSession]   = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [tab, setTab]                   = useState("stok"); // "stok" | "transfer" | "promo"
  const [closingEvent, setClosingEvent] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [evRes, sessionRes] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/kasir/events/${id}/sessions/open`),
      ]);
      setEvent(evRes.data);
      setOpenSession(sessionRes.data || null);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal mengambil data event");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCloseEvent = async () => {
    const ok = await dialog.confirm("Tutup Event?\n\nPERHATIAN: Setelah ditutup, SEMUA SISA STOK yang tidak terjual akan OTOMATIS DIKEMBALIKAN ke gudang pusat.", { title: "Tutup Event" });
    if (!ok) return;
    try {
      setClosingEvent(true);
      await api.post(`/events/${id}/close`);
      await fetchAll();
    } catch (e) {
      await dialog.alert(e?.response?.data?.message || "Gagal menutup event", { title: "Error" });
    } finally {
      setClosingEvent(false);
    }
  };

  const handleResetSales = async () => {
    const ok = await dialog.confirm("Reset Penjualan?\n\nPERHATIAN: Semua data transaksi, sesi kasir, dan rekonsiliasi kas pada event ini akan dihapus secara permanen. Stok akan dikembalikan ke posisi awal (hanya di internal event ini).", { title: "Reset Penjualan", confirmText: "Reset", confirmStyle: "danger" });
    if (!ok) return;
    try {
      setClosingEvent(true); // Reuse the same loading state for simplicity
      await api.post(`/events/${id}/reset-sales`);
      await fetchAll();
      await dialog.alert("Penjualan berhasil di-reset. Stok kembali utuh.");
    } catch (e) {
      await dialog.alert(e?.response?.data?.message || "Gagal mereset penjualan", { title: "Error" });
    } finally {
      setClosingEvent(false);
    }
  };

  const handleOpenSession = async () => {
    try {
      const res = await api.post(`/kasir/events/${id}/sessions/open`);
      navigate(`/kasir/${res.data?.session?.id || res.data?.id}`);
    } catch (e) {
      await dialog.alert(e?.response?.data?.message || "Gagal membuka kasir", { title: "Error" });
    }
  };

  if (loading && !event) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-zinc-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Memuat detail event...
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
        {error}
      </div>
    );
  }

  if (!event) return null;

  const status     = STATUS_MAP[event.status] || STATUS_MAP.draft;
  const StatusIcon = status.icon;
  const isClosed   = event.status === "closed";
  const isDraft    = event.status === "draft";

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      {/* Header */}
      <header className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/events")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
          >
            <ArrowLeft className="h-4 w-4 text-zinc-600" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
                status.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' :
                status.tone === 'rose' ? 'bg-rose-50 text-rose-700 ring-rose-600/20' :
                'bg-zinc-50 text-zinc-700 ring-zinc-500/20'
              }`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </span>
              <span className="text-zinc-300">·</span>
              <span className="flex items-center gap-1 text-[11px] font-medium text-zinc-500">
                <CalendarDays className="h-3.5 w-3.5" />
                {event.start_date} — {event.end_date}
              </span>
            </div>
            <h1 className="mt-2 text-xl font-semibold tracking-tight text-zinc-900 md:text-2xl">
              {event.name}
            </h1>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link to={`/rekap/event/${id}`} className="hidden sm:inline-flex items-center justify-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2 text-[13px] font-medium text-indigo-700 shadow-sm hover:bg-indigo-100 transition-colors focus:outline-none focus:ring-1 focus:ring-indigo-500" title="Lihat Rekap Penjualan">
              <BarChart3 className="h-4 w-4" />
              <span>Rekap Penjualan</span>
            </Link>
            <button onClick={fetchAll} disabled={loading} className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-2 text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {!isClosed && isAdmin && (
              <>
                <button
                  onClick={handleResetSales}
                  disabled={closingEvent || !!openSession}
                  className="hidden sm:inline-flex items-center justify-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-[13px] font-medium text-amber-700 shadow-sm hover:bg-amber-100 transition-colors focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50"
                  title={openSession ? "Tutup kasir terlebih dahulu" : "Reset Data Penjualan Event Ini"}
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Reset Penjualan</span>
                </button>
                <button
                  onClick={handleCloseEvent}
                  disabled={closingEvent || !!openSession}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-[13px] font-medium text-rose-700 shadow-sm hover:bg-rose-100 transition-colors focus:outline-none focus:ring-1 focus:ring-rose-500 disabled:opacity-50"
                  title={openSession ? "Tutup kasir terlebih dahulu" : "Tutup event"}
                >
                  {closingEvent ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Tutup Event
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
          {error}
        </div>
      )}

      {/* Open session warning */}
      {openSession && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-amber-200 bg-white text-amber-600 shadow-sm">
                <ShoppingCart className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-amber-900">Sesi Kasir Sedang Terbuka</div>
                <div className="mt-0.5 text-[12px] font-medium text-amber-700">
                  Sesi #{openSession.id} dibuka sejak {formatDateTime(openSession.opened_at)}
                </div>
              </div>
            </div>
            <Link
              to={`/kasir/${openSession.id}`}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-[13px] font-bold text-white shadow-sm hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 shrink-0"
            >
              <ShoppingCart className="h-4 w-4" />
              Buka Layar Kasir
            </Link>
          </div>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiMini label="Total Produk" value={event.stocks?.length ?? 0} />
        <KpiMini label="Total Sesi Kasir" value={event.total_sessions ?? 0} />
        <KpiMini
          label="Stok Tersisa Fisik"
          value={(event.stocks || []).reduce((s, x) => s + Number(x.qty_remaining || 0), 0)}
        />
        <KpiMini
          label="Stok Terjual"
          value={(event.stocks || []).reduce(
            (s, x) => s + (Number(x.qty_transferred || 0) - Number(x.qty_remaining || 0) - Number(x.qty_returned || 0)),
            0
          )}
        />
      </div>

      {/* Main area */}
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
        {/* Tabs */}
        <div className="flex border-b border-zinc-200 bg-zinc-50/50">
          <TabBtn active={tab === "stok"} onClick={() => setTab("stok")}>
            <Warehouse className="h-4 w-4" /> Stok Berjalan
          </TabBtn>
          {!isClosed && isAdmin && (
            <>
              <TabBtn active={tab === "transfer"} onClick={() => setTab("transfer")}>
                <PackagePlus className="h-4 w-4" /> Transfer Masuk
              </TabBtn>
              <TabBtn active={tab === "promo"} onClick={() => setTab("promo")}>
                <Tag className="h-4 w-4" /> Atur Diskon Event
              </TabBtn>
            </>
          )}
        </div>

        {tab === "stok" && (
          <StockTab
            stocks={event.stocks || []}
            isClosed={isClosed}
            onPriceUpdated={fetchAll}
            isAdmin={isAdmin}
          />
        )}
        {tab === "transfer" && !isClosed && (
          <TransferTab eventId={id} isDraft={isDraft} onTransferred={fetchAll} />
        )}
        {tab === "promo" && !isClosed && isAdmin && (
          <PromoTab stocks={event.stocks || []} onPriceUpdated={fetchAll} />
        )}
      </div>

      {/* Kasir action */}
      {!isClosed && !openSession && event.stocks?.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[14px] font-semibold text-zinc-900">Siap untuk berjualan?</div>
              <div className="mt-1 text-[12px] text-zinc-500">
                Pastikan seluruh stok sudah ditransfer dan harga khusus event sudah diatur sebelum memulai sesi kasir.
              </div>
            </div>
            <button onClick={handleOpenSession} className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-5 py-2.5 text-[13px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900 shrink-0">
              <ShoppingCart className="h-4 w-4" />
              Mulai Sesi Kasir Baru
            </button>
          </div>
        </div>
      )}

      {isDraft && (event.stocks?.length ?? 0) === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
            <p className="text-[13px] font-medium text-amber-800 leading-relaxed">
              Event masih berstatus Draft. Lakukan Transfer Stok dari gudang utama ke event ini untuk mengaktifkan statusnya menjadi Aktif dan bisa berjualan.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stock Tab ────────────────────────────────────────────────

function StockTab({ stocks, isClosed, onPriceUpdated, isAdmin }) {
  if (stocks.length === 0) {
    return (
      <div className="p-12 text-center">
        <h3 className="text-sm font-semibold text-zinc-900">Belum ada stok</h3>
        <p className="mt-1 text-[13px] text-zinc-500 max-w-sm mx-auto">
          Gunakan tab Transfer Masuk untuk mengalokasikan stok barang dari gudang utama ke event ini.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-[13px]">
        <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
          <tr>
            <th className="px-4 py-3 font-medium w-10">Img</th>
            <th className="px-4 py-3 font-medium">Informasi Produk</th>
            <th className="px-4 py-3 font-medium text-center">Dikirim</th>
            <th className="px-4 py-3 font-medium text-center">Laku</th>
            <th className="px-4 py-3 font-medium text-center">Sisa</th>
            <th className="px-4 py-3 font-medium text-right">Harga Satuan</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 bg-white">
          {stocks.map((s) => (
            <StockRow key={s.id} stock={s} isClosed={isClosed} onPriceUpdated={onPriceUpdated} isAdmin={isAdmin} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StockRow({ stock, isClosed, onPriceUpdated, isAdmin }) {
  const dialog = useDialog();
  const [editing, setEditing]   = useState(false);
  const [price, setPrice]       = useState(String(stock.price || 0));
  const [saving, setSaving]     = useState(false);

  const sold = Number(stock.qty_transferred || 0) - Number(stock.qty_remaining || 0) - Number(stock.qty_returned || 0);

  const handleSavePrice = async () => {
    const parsed = Number(price);
    if (isNaN(parsed) || parsed < 0) { await dialog.alert("Harga tidak valid", { title: "Error" }); return; }
    try {
      setSaving(true);
      await api.patch(`/events/${stock.event_id}/stocks/${stock.id}/price`, { 
        price: parsed, 
        promo_min_qty: stock.promo_min_qty, 
        promo_price: stock.promo_price 
      });
      setEditing(false);
      onPriceUpdated();
    } catch (e) {
      await dialog.alert(e?.response?.data?.message || "Gagal update harga", { title: "Error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="hover:bg-zinc-50/50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
          {stock.image_url ? (
            <img src={stock.image_url} alt={stock.product_name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] font-bold text-zinc-400 uppercase">
              {(stock.product_name || "?").slice(0, 2)}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-zinc-900 leading-tight">
            {stock.product_name}
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5 uppercase tracking-widest font-mono">
            {stock.kode_barang} {stock.jenis ? `· ${stock.jenis}` : ""}
          </div>
          {Number(stock.promo_min_qty) > 0 && (
            <div className="mt-1 flex">
              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                <Tag className="h-2.5 w-2.5" /> Beli {stock.promo_min_qty} = {fmtRp(stock.promo_price)}
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-center text-[13px] font-medium tabular-nums text-zinc-500">
        {stock.qty_transferred}
      </td>
      <td className="px-4 py-3 text-center text-[13px] font-bold tabular-nums">
        <span className={sold > 0 ? "text-emerald-600" : "text-zinc-400"}>{sold}</span>
      </td>
      <td className="px-4 py-3 text-center text-[13px] font-bold tabular-nums">
        <span className={stock.qty_remaining > 0 ? "text-zinc-800" : "text-rose-600"}>
          {stock.qty_remaining}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex items-center justify-end gap-1.5">
            <input className="w-24 rounded-md border border-zinc-300 px-2 py-1 text-[13px] text-right tabular-nums focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm" type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
            <button onClick={handleSavePrice} disabled={saving} className="rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-bold text-white shadow-sm hover:bg-zinc-800 disabled:opacity-50 transition-colors">
              {saving ? "..." : "OK"}
            </button>
            <button onClick={() => { setEditing(false); setPrice(String(stock.price || 0)); }} className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-bold text-zinc-600 shadow-sm hover:bg-zinc-50 transition-colors">
              ✕
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <span className="text-[13px] font-bold text-zinc-900 tabular-nums">
              {fmtRp(stock.price)}
            </span>
            {!isClosed && isAdmin && (
              <button onClick={() => setEditing(true)} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors focus:outline-none" title="Edit harga">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Transfer Tab ─────────────────────────────────────────────
function TransferTab({ eventId, onTransferred }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch]     = useState("");
  const [basket, setBasket]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState("");
  const [zeroPriceModal, setZeroPriceModal] = useState(null);
  const [sortMode, setSortMode] = useState("default"); // 'default' | 'asc' | 'desc'

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get("/events/products");
      setProducts(res.data || []);
    } catch (e) {
      setError("Gagal mengambil produk gudang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  let filtered = products.filter((p) => {
    const q = search.toLowerCase();
    return (
      (p.nama_barang || "").toLowerCase().includes(q) ||
      (p.kode_barang || "").toLowerCase().includes(q)
    );
  });

  if (sortMode === "asc") {
    filtered = [...filtered].sort((a, b) => (a.nama_barang || "").localeCompare(b.nama_barang || ""));
  } else if (sortMode === "desc") {
    filtered = [...filtered].sort((a, b) => (b.nama_barang || "").localeCompare(a.nama_barang || ""));
  }

  const setQty = (pid, val) => {
    const raw = val.replace(/\D/g, "");
    setBasket((b) => ({ ...b, [pid]: { ...(b[pid] || { price: "" }), qty: raw } }));
  };

  const setPrice = (pid, val) => {
    const raw = val.replace(/\D/g, "");
    setBasket((b) => ({ ...b, [pid]: { ...(b[pid] || { qty: "" }), price: raw } }));
  };


  const fmtNumber = (val) => {
    if (!val) return "";
    return new Intl.NumberFormat("id-ID").format(val);
  };

  const basketItems = Object.entries(basket)
    .filter(([, v]) => Number(v.qty) > 0)
    .map(([pid, v]) => ({ product_id: Number(pid), qty: Number(v.qty), price: Number(v.price), promo_min_qty: Number(v.promo_min_qty || 0), promo_price: Number(v.promo_price || 0) }));

  const handleTransfer = async () => {
    if (basketItems.length === 0) { setError("Pilih minimal 1 produk dan isi qty"); return; }
    const zeros = basketItems.filter((x) => x.price <= 0);
    if (zeros.length > 0) {
      const itemsToConfirm = zeros.map(z => {
        const p = products.find(prod => prod.id === z.product_id);
        return { ...z, name: p?.nama_barang || "Produk tak dikenal", code: p?.kode_barang || "" };
      });
      setZeroPriceModal(itemsToConfirm);
      return;
    }
    await executeTransfer(basketItems);
  };

  const executeTransfer = async (items) => {
    try {
      setSubmitting(true);
      await api.post(`/events/${eventId}/stocks`, { items });
      setBasket({});
      setError("");
      setZeroPriceModal(null);
      onTransferred();
      fetchProducts();
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal transfer stok");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-[560px]">
      {error && (
        <div className="m-4 mb-0 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700 animate-fade-in shrink-0">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 p-4 border-b border-zinc-100 shrink-0">
        <input
          className="w-full max-w-sm rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
          placeholder="Cari produk gudang untuk ditransfer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {basketItems.length > 0 && (
          <button onClick={handleTransfer} disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900 shrink-0">
            {submitting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <PackagePlus className="h-4 w-4" />
            )}
            {submitting ? "Mentransfer..." : `Transfer ${basketItems.length} Produk`}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-x-auto min-h-0 bg-white">
        <table className="min-w-full text-left text-[13px]">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-4 py-3 font-medium w-10">Img</th>
              <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-zinc-900 group" onClick={() => {
                if (sortMode === "default") setSortMode("asc");
                else if (sortMode === "asc") setSortMode("desc");
                else setSortMode("default");
              }}>
                <div className="flex items-center gap-2">
                  Produk
                  <span className={`text-[10px] ${sortMode !== "default" ? "text-zinc-900 font-bold" : "text-zinc-400 group-hover:text-zinc-600"}`}>
                    {sortMode === "asc" ? "A-Z ▲" : sortMode === "desc" ? "Z-A ▼" : "—"}
                  </span>
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-right">Tersedia (Pusat)</th>
              <th className="px-4 py-3 font-medium text-center w-28">Qty Transfer</th>
              <th className="px-4 py-3 font-medium text-center w-36">Harga Jual (Rp)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-16 text-center text-zinc-400">
                  <div className="flex flex-col items-center gap-3">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                    <span className="text-[13px] font-medium">Memuat stok gudang...</span>
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-16 text-center text-zinc-400 text-[13px] font-medium">Produk tidak ditemukan</td>
              </tr>
            ) : (
              filtered.map((p) => {
                const bItem   = basket[p.id] || { qty: "", price: "" };
                const qtyNum  = Number(bItem.qty) || 0;
                const soldOut = Number(p.stock ?? 0) <= 0;
                const inBasket = qtyNum > 0;
                const spareNum = Number(p.spare ?? p.stock);
                const exceedsSpare = qtyNum > spareNum;
                const allocInfo = [];
                if (exceedsSpare) {
                    let kekurangan = qtyNum - spareNum;
                    let allocShopee = Number(p.alloc_shopee ?? 0);
                    let allocTokped = Number(p.alloc_tokopedia ?? 0);
                    if (kekurangan > 0 && allocShopee > 0) {
                        let potong = Math.min(kekurangan, allocShopee);
                        allocInfo.push(`Shopee (-${potong})`);
                        kekurangan -= potong;
                    }
                    if (kekurangan > 0 && allocTokped > 0) {
                        let potong = Math.min(kekurangan, allocTokped);
                        allocInfo.push(`Tokped (-${potong})`);
                    }
                }

                return (
                  <tr key={p.id} className={`transition-colors ${inBasket ? 'bg-emerald-50/40' : soldOut ? 'opacity-50 grayscale' : 'hover:bg-zinc-50/50 bg-white'}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-50">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.nama_barang} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-[10px] font-bold text-zinc-400 uppercase">{(p.nama_barang || "?").slice(0, 2)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-bold text-zinc-900 leading-tight">{p.nama_barang}</div>
                        <div className="mt-0.5 text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{p.kode_barang}</div>
                      </div>
                      {exceedsSpare && allocInfo.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5 rounded bg-amber-100 px-2 py-1 max-w-fit">
                          <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />
                          <span className="text-[10px] font-bold text-amber-800">
                            Diambil dr alokasi: {allocInfo.join(", ")}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[13px] tabular-nums">
                      <span className={`font-bold ${soldOut ? 'text-rose-600' : 'text-zinc-800'}`} title={`Fisik: ${p.stock} | Spare: ${spareNum}`}>
                        {p.stock ?? 0}
                      </span>
                      {p.jenis && <span className="ml-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400">{p.jenis}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <input
                        type="text"
                        className={`w-full rounded-md border px-2 py-1.5 text-center text-[13px] font-bold tabular-nums shadow-sm outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 disabled:opacity-50 ${exceedsSpare ? "border-amber-300 bg-amber-50 text-amber-900 focus:ring-amber-500" : "border-zinc-300 bg-white"}`}
                        placeholder="0"
                        value={fmtNumber(bItem.qty)}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (Number(val) > Number(p.stock)) return;
                          setQty(p.id, val);
                        }}
                        disabled={soldOut}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <input
                        type="text"
                        className="w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-right text-[13px] font-bold tabular-nums shadow-sm outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 disabled:opacity-50"
                        placeholder="Harga"
                        value={fmtNumber(bItem.price)}
                        onChange={(e) => setPrice(p.id, e.target.value)}
                        disabled={soldOut}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {zeroPriceModal && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-md rounded-xl border border-zinc-200 bg-white shadow-2xl animate-fade-in overflow-hidden">
            <div className="bg-amber-50 p-5 border-b border-amber-100 flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0" />
              <div>
                <h2 className="text-[15px] font-bold text-amber-900">Konfirmasi Harga Rp 0</h2>
                <p className="text-[13px] font-medium text-amber-700 mt-1 leading-relaxed">
                  Ada produk tanpa harga jual (Rp 0). Lanjutkan proses?
                </p>
              </div>
            </div>
            <div className="p-4 max-h-60 overflow-y-auto bg-zinc-50/50">
              <ul className="space-y-3">
                {zeroPriceModal.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[13px] text-zinc-700">
                    <span className="shrink-0 mt-0.5 font-black text-zinc-400">•</span>
                    <div>
                      <div className="font-semibold text-zinc-900">{item.name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{item.code} | Qty: {item.qty}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-3 p-4 border-t border-zinc-100 bg-white">
              <button 
                onClick={() => setZeroPriceModal(null)} 
                className="flex-1 rounded-md border border-zinc-200 bg-white py-2.5 text-[13px] font-bold text-zinc-700 hover:bg-zinc-50 transition-colors shadow-sm"
              >
                Batal
              </button>
              <button 
                onClick={() => executeTransfer(basketItems)} 
                disabled={submitting} 
                className="flex-1 rounded-md bg-amber-500 py-2.5 text-[13px] font-bold text-white shadow-sm hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                {submitting ? "Memproses..." : "Ya, Lanjutkan"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Promo Tab ─────────────────────────────────────────────

function PromoTab({ stocks, onPriceUpdated }) {
  const dialog = useDialog();
  const [selectedStocks, setSelectedStocks] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  
  // Modal state
  const [discountType, setDiscountType] = useState("percent"); // 'percent' or 'fixed'
  const [discountValue, setDiscountValue] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Custom prices preview
  const [previewPrices, setPreviewPrices] = useState({});

  useEffect(() => {
    const val = Number(discountValue) || 0;
    const newPreviews = {};
    selectedStocks.forEach(stockId => {
      const stock = stocks.find(s => s.id === stockId);
      if (!stock) return;
      
      if (!val) {
        newPreviews[stockId] = stock.price;
        return;
      }
      
      let calculatedPrice = stock.price;
      if (discountType === "percent") {
        const pct = Math.min(100, Math.max(0, val));
        const discountAmt = stock.price * pct / 100;
        calculatedPrice = Math.round(stock.price - discountAmt);
      } else {
        calculatedPrice = Math.max(0, stock.price - val);
      }
      newPreviews[stockId] = calculatedPrice;
    });
    setPreviewPrices(newPreviews);
  }, [discountValue, discountType, selectedStocks, stocks]);

  const getTiers = (stock) => {
    try {
      if (typeof stock.promo_tiers === 'string') return JSON.parse(stock.promo_tiers) || [];
      if (Array.isArray(stock.promo_tiers)) return stock.promo_tiers;
    } catch(e) {}
    return [];
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedStocks);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedStocks(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedStocks.size === stocks.length) {
      setSelectedStocks(new Set());
    } else {
      setSelectedStocks(new Set(stocks.map(s => s.id)));
    }
  };

  const handleApplyMassDiscount = async () => {
    try {
      setSaving(true);
      const promises = Array.from(selectedStocks).map(async (stockId) => {
        const stock = stocks.find(s => s.id === stockId);
        if (!stock) return;
        const finalPrice = previewPrices[stockId] ?? stock.price;
        const newTiers = [{ min_qty: 1, price: finalPrice }];
        await api.patch(`/events/${stock.event_id}/stocks/${stock.id}/price`, {
          price: stock.price,
          promo_tiers: newTiers
        });
      });

      await Promise.all(promises);
      setModalOpen(false);
      setSelectedStocks(new Set());
      setDiscountValue("");
      setPreviewPrices({});
      onPriceUpdated();
      await dialog.alert("Diskon massal berhasil diterapkan.");
    } catch (e) {
      await dialog.alert("Gagal menerapkan diskon massal.");
    } finally {
      setSaving(false);
    }
  };

  const handleClearDiscounts = async () => {
    const ok = await dialog.confirm("Hapus seluruh aturan diskon pada produk terpilih?");
    if (!ok) return;
    try {
      setSaving(true);
      const promises = Array.from(selectedStocks).map(async (stockId) => {
        const stock = stocks.find(s => s.id === stockId);
        if (!stock) return;
        await api.patch(`/events/${stock.event_id}/stocks/${stock.id}/price`, {
          price: stock.price,
          promo_tiers: []
        });
      });
      await Promise.all(promises);
      setSelectedStocks(new Set());
      onPriceUpdated();
    } catch (e) {
      await dialog.alert("Gagal menghapus diskon.");
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewChange = (stockId, val) => {
    setPreviewPrices(prev => ({
      ...prev,
      [stockId]: Number(val)
    }));
  };

  if (stocks.length === 0) {
    return (
      <div className="p-12 text-center text-[13px] font-medium text-zinc-500">
        Belum ada stok. Transfer stok terlebih dahulu.
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4 animate-fade-in bg-white min-h-[500px]">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
        <div className="flex gap-3 items-start">
          <Tag className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[14px] font-semibold text-indigo-900">Program Diskon Massal</h3>
            <p className="text-[12px] font-medium text-indigo-700/80 mt-1">
              Pilih produk dan tentukan potongan harga serentak. Harga dasar tidak akan berubah, diskon ditambahkan sebagai promo tier.
            </p>
          </div>
        </div>
      </div>
      
      {/* Mass Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-md border border-zinc-200 bg-zinc-50 p-3 shadow-sm gap-3">
        <label className="flex items-center gap-2 cursor-pointer ml-1 select-none group">
          <input 
            type="checkbox" 
            className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 w-4 h-4 cursor-pointer"
            checked={stocks.length > 0 && selectedStocks.size === stocks.length}
            onChange={toggleSelectAll}
          />
          <span className="text-[13px] font-bold text-zinc-700 group-hover:text-zinc-900 transition-colors">Pilih Semua ({selectedStocks.size})</span>
        </label>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <button 
            disabled={selectedStocks.size === 0 || saving}
            onClick={handleClearDiscounts}
            className="inline-flex flex-1 sm:flex-none items-center justify-center rounded-md border border-rose-200 bg-white px-4 py-2 text-[13px] font-bold text-rose-600 shadow-sm hover:bg-rose-50 transition-colors disabled:opacity-50"
          >
            Hapus Diskon
          </button>
          <button 
            disabled={selectedStocks.size === 0 || saving}
            onClick={() => setModalOpen(true)}
            className="inline-flex flex-1 sm:flex-none items-center justify-center rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 focus:ring-1 focus:ring-zinc-900 focus:outline-none"
          >
            Terapkan Diskon Baru
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {stocks.map((stock) => {
          const stockTiers = getTiers(stock);
          const isSelected = selectedStocks.has(stock.id);
          const activePromo = stockTiers.find(t => t.min_qty === 1);
          
          return (
            <div 
              key={stock.id} 
              onClick={() => toggleSelect(stock.id)}
              className={`relative flex flex-col rounded-lg border p-4 transition-all cursor-pointer select-none ${isSelected ? 'border-zinc-900 bg-zinc-50/50 ring-1 ring-zinc-900 shadow-md scale-[0.98]' : 'border-zinc-200 bg-white hover:border-zinc-300 shadow-sm'}`}
            >
              <div className="absolute top-3 right-3 pointer-events-none">
                <input 
                  type="checkbox" 
                  checked={isSelected}
                  readOnly
                  className="rounded border-zinc-300 text-zinc-900 w-4 h-4"
                />
              </div>

              <div className="flex flex-col items-center gap-3">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-100 bg-zinc-50 shadow-inner">
                  {stock.image_url ? (
                    <img src={stock.image_url} alt={stock.product_name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[13px] font-bold text-zinc-400 uppercase">{(stock.product_name || "?").slice(0, 2)}</span>
                  )}
                </div>
                <div className="w-full text-center">
                  <div className="truncate text-[13px] font-bold text-zinc-900 leading-tight" title={stock.product_name}>{stock.product_name}</div>
                  <div className="text-[10px] text-zinc-400 font-mono uppercase tracking-widest mt-0.5">{stock.kode_barang}</div>
                  
                  {activePromo ? (
                    <div className="mt-2 flex flex-col items-center">
                      <div className="text-[11px] font-medium text-zinc-400 line-through decoration-rose-400 decoration-wavy">
                        {fmtRp(stock.price)}
                      </div>
                      <div className="text-[15px] font-black text-emerald-600 flex items-center gap-1.5 tabular-nums mt-0.5">
                        {fmtRp(activePromo.price)}
                        <span className="inline-flex items-center rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-black tracking-widest text-emerald-700 border border-emerald-200">PROMO</span>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-[15px] font-bold text-zinc-900 tabular-nums">
                      {fmtRp(stock.price)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {modalOpen && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-3xl rounded-xl border border-zinc-200 bg-white shadow-2xl animate-fade-in overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/50 px-6 py-4 shrink-0">
              <h2 className="text-[15px] font-bold text-zinc-900">Hitung & Sesuaikan Diskon</h2>
              <button onClick={() => setModalOpen(false)} className="rounded-md p-1.5 transition-colors hover:bg-zinc-100 text-zinc-500 focus:outline-none">
                <XCircle className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex flex-col md:flex-row min-h-0">
              <div className="p-6 md:w-[45%] border-b md:border-b-0 md:border-r border-zinc-100 bg-white">
                <div className="mb-5">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Tipe Diskon</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => setDiscountType("percent")}
                      className={`py-2 rounded-md font-bold text-[13px] border shadow-sm transition-all focus:outline-none focus:ring-1 focus:ring-zinc-900 ${discountType === 'percent' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                    >
                      Persentase (%)
                    </button>
                    <button 
                      onClick={() => setDiscountType("fixed")}
                      className={`py-2 rounded-md font-bold text-[13px] border shadow-sm transition-all focus:outline-none focus:ring-1 focus:ring-zinc-900 ${discountType === 'fixed' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                    >
                      Potongan (Rp)
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Nilai Dasar (Opsional)</label>
                  <div className="relative">
                    {discountType === 'fixed' && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400 font-bold text-sm">Rp</div>}
                    <input 
                      type="number" min="0" 
                      className={`w-full rounded-md border border-zinc-300 bg-white py-2.5 text-base font-bold text-zinc-900 tabular-nums outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm ${discountType === 'fixed' ? 'pl-10' : 'pr-10 pl-3'}`}
                      placeholder="0"
                      value={discountValue}
                      onChange={e => setDiscountValue(e.target.value)}
                    />
                    {discountType === 'percent' && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-zinc-400 font-bold text-sm">%</div>}
                  </div>
                  {discountType === 'percent' && Number(discountValue) > 100 && (
                    <div className="mt-1.5 text-[11px] font-bold text-rose-600">Maksimal 100%</div>
                  )}
                  <p className="mt-3 text-[12px] font-medium text-zinc-500 leading-relaxed">
                    Angka ini menjadi dasar perhitungan massal. Anda bisa mengedit hasil akhir satu per satu pada tabel di sebelah.
                  </p>
                </div>
              </div>

              <div className="p-0 md:w-[55%] flex flex-col min-h-0 bg-zinc-50/50">
                <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-100 shrink-0 shadow-sm z-10">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Preview & Koreksi Final</div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {Array.from(selectedStocks).map(stockId => {
                    const stock = stocks.find(s => s.id === stockId);
                    if (!stock) return null;
                    const finalPrice = previewPrices[stockId] ?? stock.price;
                    const pctOff = stock.price > 0 ? Math.round(((stock.price - finalPrice) / stock.price) * 100) : 0;

                    return (
                      <div key={stockId} className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-md hover:border-zinc-300 transition-colors shadow-sm">
                        <div className="min-w-0 pr-3">
                          <div className="truncate text-[13px] font-bold text-zinc-900 leading-tight">{stock.product_name}</div>
                          <div className="text-[11px] font-medium text-zinc-400 line-through mt-0.5">{fmtRp(stock.price)}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {pctOff > 0 && <div className="text-[11px] font-black text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded ring-1 ring-inset ring-rose-600/20">{pctOff}%</div>}
                          <input 
                            type="number"
                            min="0"
                            className="w-24 rounded border border-zinc-300 bg-white px-2 py-1.5 text-right text-[13px] font-bold tabular-nums text-emerald-700 outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-inner"
                            value={previewPrices[stockId] === undefined ? "" : previewPrices[stockId]}
                            onChange={(e) => handlePreviewChange(stockId, e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="p-4 bg-white border-t border-zinc-200 shrink-0">
              <button 
                onClick={handleApplyMassDiscount} 
                disabled={saving || (discountType === 'percent' && Number(discountValue) > 100)} 
                className="w-full rounded-md bg-zinc-900 py-3.5 text-[14px] font-bold text-white shadow-sm transition-all hover:bg-zinc-800 disabled:opacity-40 disabled:scale-100 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-offset-2"
              >
                {saving ? "Memproses..." : `Simpan Diskon (${selectedStocks.size} Produk)`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function KpiMini({ label, value }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm flex flex-col justify-center">
      <div className="text-2xl font-bold tracking-tight text-zinc-900 tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold transition-all border-b-2 mb-[-1px] focus:outline-none ${
        active
          ? "border-zinc-900 text-zinc-900"
          : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

function fmtRp(n) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

export default EventDetail;